import { useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import BundlesPage from "./bundles.page";
import BundleAddDialog from "../../components/BundleAddDialog";
import BundleEditDialog from "../../components/BundleEditDialog";
import {
  getBundleListing,
  getBundle,
  getBundleCompanies,
  setBundleStatus,
  deleteBundle,
} from "../../redux/bundles.action";
import { clearSelected } from "../../redux/bundles.slice";
import AccessRights from "../../../../configs/constants/accessRights";
import ConfirmationDialog from "../../../../components/Dialog/confirmation";

export default function BundlesContainer() {
  const dispatch = useDispatch();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addPrefill, setAddPrefill] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    id: null,
  });

  const rows = useSelector((state) => state.bundles.listingTable.records);
  const isLoading = useSelector((state) => state.bundles.isLoading);
  const totalRecords = useSelector((state) => state.bundles.listingTable.totalRecords);
  const bundleSelected = useSelector((state) => state.bundles.bundleSelected);
  const listingFailed = useSelector((state) => state.bundles.listingFailed);

  // The listing read failed, nothing is loading over it, AND no rows ever arrived: only then
  // draw the inline Retry panel instead of the table. Requiring an empty table mirrors
  // catalogue-state.js's `!loaded` guard — a failed REFRESH of a list that did arrive (a page
  // change or search that blipped) leaves the cached rows on screen rather than replacing a
  // still-valid table with an error that contradicts itself. A refetch in flight is not a
  // failure either, so a stale panel never sits on top of a load that might still succeed.
  const hasFailedToLoad = listingFailed && !isLoading && rows.length === 0;

  const canEdit = useSelector((state) => state.session.accessRights.includes(AccessRights.BUNDLE_EDIT));

  const fetchData = (payload) => {
    dispatch(getBundleListing(payload));
  };

  // Debounce the raw search input into the committed search term.
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData({
      page,
      rowsPerPage,
      search,
    });
  }, [page, rowsPerPage, search]);

  const refresh = () => {
    fetchData({
      page,
      rowsPerPage,
      search,
    });
  };

  const onSubmit = () => {
    setPage(0);
    fetchData({
      page: 0,
      rowsPerPage,
      search,
    });
  };

  const handleAddDialogOpen = () => {
    setAddPrefill(null); // a plain "Add Bundle" always starts empty
    setIsAddDialogOpen(true);
  };

  const handleAddDialogClose = () => {
    setIsAddDialogOpen(false);
  };

  const onSearchChange = (value) => {
    setSearchInput(value);
  };

  const onPageClick = (value) => {
    if (page !== value) {
      setPage(value);
    }
  };

  const handleItemPerPageChange = (value) => {
    setPage(0);
    setRowsPerPage(value);
  };

  const handleOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(id);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onEnableClick = (id) => {
    dispatch(setBundleStatus({ id, isActive: true })).then(() => refresh());
  };

  const onDisableClick = (id) => {
    dispatch(setBundleStatus({ id, isActive: false })).then(() => refresh());
  };

  const onEditClick = (id) => {
    setSelectedRow(id);
    setIsEditDialogOpen(true);
    dispatch(getBundle({ id }));
    // What an in-place Save would re-flatten. Independent of getBundle: if it fails the
    // dialog still opens, just without the warning line.
    dispatch(getBundleCompanies({ id }));
  };

  // "Duplicate" no longer clones server-side: the backend rejects a bundle whose module set
  // matches an existing one, so a verbatim clone could only ever 400. Instead pre-fill the
  // Add dialog with the source's name + modules and make the admin change the set first.
  // The listing row carries only moduleCount, so the composition has to be fetched.
  const onDuplicateClick = (id) => {
    dispatch(getBundle({ id }))
      .then(unwrapResult)
      .then((record) => {
        setAddPrefill({
          sourceName: record.name,
          name: `${record.name} (copy)`,
          description: record.description || "",
          moduleIds: record.moduleIds || [],
        });
        setIsAddDialogOpen(true);
      })
      .catch(() => {});
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    dispatch(clearSelected());
    setSelectedRow(null);
  };

  // An assigned bundle cannot be deleted — the row's Delete action is disabled with a reason
  // (bundles.page.js), so this only ever fires for an unassigned one. The guard is belt-and-
  // braces: never open the confirm for a bundle a company still holds.
  const onDeleteClick = (row) => {
    if (row.companyAssigned > 0) {
      return;
    }
    setConfirmDialog({
      isOpen: true,
      id: row.id,
      title: `Delete bundle "${row.name}"?`,
    });
  };

  const handleConfirmClose = () => {
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const handleConfirmProceed = () => {
    const { id } = confirmDialog;
    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    dispatch(deleteBundle({ id })).then(() => refresh());
  };

  return (
    <>
      <BundlesPage
        onCreateClick={handleAddDialogOpen}
        onPageClick={onPageClick}
        handleItemPerPageChange={handleItemPerPageChange}
        rows={rows}
        totalRecords={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        selectedRow={selectedRow}
        handleOpen={handleOpen}
        handleClose={handleClose}
        onEnableClick={onEnableClick}
        onDisableClick={onDisableClick}
        onEditClick={onEditClick}
        onDuplicateClick={onDuplicateClick}
        onDeleteClick={onDeleteClick}
        canEdit={canEdit}
        search={searchInput}
        onSearchChange={onSearchChange}
        hasFailedToLoad={hasFailedToLoad}
        onRetry={refresh}
      />
      <BundleAddDialog
        open={isAddDialogOpen}
        onClose={handleAddDialogClose}
        onCreate={onSubmit}
        prefill={addPrefill}
      />
      {isEditDialogOpen && bundleSelected !== null && (
        <BundleEditDialog
          open={!!bundleSelected}
          onClose={handleEditClose}
          record={bundleSelected}
          id={selectedRow}
          onEdit={onSubmit}
          canEdit={canEdit}
        />
      )}
      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        handleClose={handleConfirmClose}
        handleProceed={handleConfirmProceed}
        title={confirmDialog.title}
      />
    </>
  );
}
